/**
 * Our own logo and favicon files per brand, for the brand details page.
 *
 * These are the assets already in this repo — `components/TappLogo/logos/logo-<code>.svg`
 * and the generated `assets/icons/generated/apple-touch-icon-<code>.png` — not anything
 * from a brand kit. Brand kit files are not re-hosted; those stay Drive links.
 *
 * Most theme codes name their own files. The exceptions are the codes that are a style
 * namespace rather than a title, plus a handful the asset files spell differently.
 *
 * See `stories.ts` for why playground-only helpers live in this directory.
 */

const ICON_DIR = '../assets/icons/generated';
const LOGO_DIR = '../components/TappLogo/logos';

// eager: these resolve to URLs, not file contents, and the page shows one of each.
const faviconUrls = import.meta.glob<string>('../assets/icons/generated/apple-touch-icon-*.png', {
  query: '?url',
  import: 'default',
  eager: true,
});

const logoUrls = import.meta.glob<string>('../components/TappLogo/logos/logo-*.svg', {
  query: '?url',
  import: 'default',
  eager: true,
});

/**
 * `adr` and `adb` are the AD regional namespaces (the red titles and the blue ED/PZC
 * ones), so they borrow a title's mark; `flu` is Fluister, whose files use its NL code.
 * A brand absent here uses its own code, and one with no file shows no logo at all —
 * `lezerij`, `tijdschrift` and `topics` have no logo in the repo.
 */
const LOGO_CODES: Record<string, string> = {
  adr: 'ad',
  adb: 'ed',
  flu: 'flnl',
};

const FAVICON_CODES: Record<string, string> = {
  adr: 'algemeen-dagblad',
  adb: 'ed',
  flu: 'flnl',
};

/** Null when the repo holds no logo for this brand, so the page can say so. */
export function logoUrlFor(brand: string) {
  const code = LOGO_CODES[brand] ?? brand;

  return logoUrls[`${LOGO_DIR}/logo-${code}.svg`] ?? null;
}

export function faviconUrlFor(brand: string) {
  const code = FAVICON_CODES[brand] ?? brand;

  return faviconUrls[`${ICON_DIR}/apple-touch-icon-${code}.png`] ?? null;
}
